import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import quizService, { Quiz, UpdateQuizData, QuestionType } from '../services/quizService';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { stripHtmlTags } from '../utils/htmlUtils';
import { 
  ArrowLeftIcon, 
  PlayIcon, 
  PencilIcon, 
  TrashIcon,
  XMarkIcon,
  PlusIcon,
  GlobeAltIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';

const QuizDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [togglingPublic, setTogglingPublic] = useState(false);
  const [showQuizOptions, setShowQuizOptions] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  
  // Enhanced filter states
  const [showFilters, setShowFilters] = useState(false);
  const [questionTypeFilter, setQuestionTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'order' | 'question' | 'type'>('order');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showCreateQuestionModal, setShowCreateQuestionModal] = useState(false);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null);
  
  // Create/Edit question form state
  const [newQuestion, setNewQuestion] = useState('');
  const [newQuestionType, setNewQuestionType] = useState<QuestionType>('multiple_choice');
  const [newCorrectAnswer, setNewCorrectAnswer] = useState('');
  const [newWrongAnswers, setNewWrongAnswers] = useState(['', '', '']);
  const [newExplanation, setNewExplanation] = useState('');
  const [savingQuestion, setSavingQuestion] = useState(false);
  // New fields for different question types
  const [newBlankText, setNewBlankText] = useState('');
  const [newAcceptableAnswers, setNewAcceptableAnswers] = useState<string[]>([]);
  const [newExpectedKeyPoints, setNewExpectedKeyPoints] = useState<string[]>(['']);
  const [newMinWords, setNewMinWords] = useState(10);
  const [newMaxWords, setNewMaxWords] = useState(500);
  
  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [editQuestions, setEditQuestions] = useState<any[]>([]);
  const [editSettings, setEditSettings] = useState<any>({
    timeLimit: undefined,
    randomizeQuestions: false,
    randomizeAnswers: false,
    showExplanations: true,
    allowRetries: true,
  });

  useEffect(() => {
    if (id) {
      loadQuiz();
    }
  }, [id]);

  const loadQuiz = async () => {
    try {
      setLoading(true);
      const quizData = await quizService.getQuiz(id!);
      setQuiz(quizData);
      
      // Initialize edit form with current data
      setEditTitle(quizData.title);
      setEditDescription(quizData.description || '');
      setEditTags(quizData.tags || []);
      setEditQuestions(quizData.questions || []);
      setEditSettings(quizData.settings || {
        timeLimit: undefined,
        randomizeQuestions: false,
        randomizeAnswers: false,
        showExplanations: true,
        allowRetries: true,
      });
    } catch (error) {
      console.error('Error loading quiz:', error);
      toast.error('Erro ao carregar quiz');
      navigate('/quizzes');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) {
      toast.error('O título não pode estar vazio');
      return;
    }

    try {
      const updateData: UpdateQuizData = {
        title: editTitle,
        description: editDescription,
        tags: editTags,
        settings: editSettings,
      };

      // Only include questions for custom quizzes
      if (quiz?.type === 'custom') {
        updateData.questions = editQuestions.map(q => ({
          question: q.question,
          correctAnswer: q.correctAnswer,
          wrongAnswers: q.wrongAnswers,
          explanation: q.explanation,
        }));
      }

      await quizService.updateQuiz(id!, updateData);
      toast.success('Quiz atualizado com sucesso!');
      setIsEditing(false);
      loadQuiz();
    } catch (error) {
      console.error('Error updating quiz:', error);
      toast.error('Erro ao atualizar quiz');
    }
  };

  const handleDeleteQuiz = async () => {
    try {
      await quizService.deleteQuiz(id!);
      toast.success('Quiz deletado com sucesso!');
      navigate('/quizzes');
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error('Erro ao deletar quiz');
    }
  };

  const handleTogglePublic = async () => {
    if (!id || !quiz) return;

    try {
      setTogglingPublic(true);
      const newPublicStatus = !quiz.isPublic;
      await quizService.updateQuiz(id, {
        isPublic: newPublicStatus,
      });
      setQuiz({ ...quiz, isPublic: newPublicStatus });
      toast.success(newPublicStatus ? 'Quiz agora é público!' : 'Quiz agora é privado!');
    } catch (error) {
      console.error('Failed to toggle public status:', error);
      toast.error('Erro ao alterar visibilidade do quiz');
    } finally {
      setTogglingPublic(false);
    }
  };

  const handleStartQuiz = () => {
    navigate(`/quiz/${id}`);
  };

  const handleAddTag = () => {
    if (newTag.trim() && !editTags.includes(newTag.trim())) {
      setEditTags([...editTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setEditTags(editTags.filter(tag => tag !== tagToRemove));
  };

  const handleAddQuestion = () => {
    setEditQuestions([
      ...editQuestions,
      {
        id: Date.now().toString(),
        question: '',
        correctAnswer: '',
        wrongAnswers: ['', '', ''],
        explanation: '',
      },
    ]);
  };

  const handleRemoveQuestion = (index: number) => {
    setEditQuestions(editQuestions.filter((_, i) => i !== index));
  };

  const handleUpdateQuestion = (index: number, field: string, value: any) => {
    const updated = [...editQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setEditQuestions(updated);
  };

  const handleUpdateWrongAnswer = (questionIndex: number, answerIndex: number, value: string) => {
    const updated = [...editQuestions];
    const wrongAnswers = [...updated[questionIndex].wrongAnswers];
    wrongAnswers[answerIndex] = value;
    updated[questionIndex] = { ...updated[questionIndex], wrongAnswers };
    setEditQuestions(updated);
  };

  const handleOpenCreateQuestionModal = (questionType: QuestionType = 'multiple_choice') => {
    setEditingQuestionIndex(null);
    setNewQuestion('');
    setNewQuestionType(questionType);
    setNewCorrectAnswer('');
    setNewWrongAnswers(['', '', '']);
    setNewExplanation('');
    setNewBlankText('');
    setNewAcceptableAnswers([]);
    setNewExpectedKeyPoints(['']);
    setNewMinWords(10);
    setNewMaxWords(500);
    setShowCreateQuestionModal(true);
  };

  const handleOpenEditQuestionModal = (index: number) => {
    const question = quiz!.questions[index];
    setEditingQuestionIndex(index);
    setNewQuestion(question.question);
    setNewQuestionType(question.questionType || 'multiple_choice');
    setNewCorrectAnswer(question.correctAnswer || '');
    setNewWrongAnswers(question.wrongAnswers || ['', '', '']);
    setNewExplanation(question.explanation || '');
    setNewBlankText(question.blankText || '');
    setNewAcceptableAnswers(question.acceptableAnswers || []);
    setNewExpectedKeyPoints(question.expectedKeyPoints || ['']);
    setNewMinWords(question.minWords || 10);
    setNewMaxWords(question.maxWords || 500);
    setShowCreateQuestionModal(true);
  };

  const handleCloseQuestionModal = () => {
    setShowCreateQuestionModal(false);
    setEditingQuestionIndex(null);
    setNewQuestion('');
    setNewQuestionType('multiple_choice');
    setNewCorrectAnswer('');
    setNewWrongAnswers(['', '', '']);
    setNewExplanation('');
    setNewBlankText('');
    setNewAcceptableAnswers([]);
    setNewExpectedKeyPoints(['']);
    setNewMinWords(10);
    setNewMaxWords(500);
  };

  const handleSaveQuestion = async () => {
    if (!newQuestion.trim()) {
      toast.error('A pergunta não pode estar vazia');
      return;
    }

    // Validation based on question type
    if (newQuestionType === 'multiple_choice') {
      if (!newCorrectAnswer.trim()) {
        toast.error('A resposta correta não pode estar vazia');
        return;
      }
      if (newWrongAnswers.filter(a => a.trim()).length < 3) {
        toast.error('Você precisa fornecer 3 respostas incorretas');
        return;
      }
    } else if (newQuestionType === 'true_false') {
      if (newCorrectAnswer !== 'Verdadeiro' && newCorrectAnswer !== 'Falso') {
        toast.error('Selecione Verdadeiro ou Falso');
        return;
      }
    } else if (newQuestionType === 'fill_blank') {
      if (!newBlankText.includes('___')) {
        toast.error('O texto deve conter ___ para indicar a lacuna');
        return;
      }
      if (!newCorrectAnswer.trim()) {
        toast.error('A resposta correta não pode estar vazia');
        return;
      }
    } else if (newQuestionType === 'essay') {
      if (!newExpectedKeyPoints.filter(p => p.trim()).length) {
        toast.error('Adicione pelo menos um ponto-chave esperado');
        return;
      }
    }

    try {
      setSavingQuestion(true);
      
      const updatedQuestions = [...quiz!.questions];
      const questionData: any = {
        question: newQuestion,
        questionType: newQuestionType,
        correctAnswer: newCorrectAnswer,
        wrongAnswers: newQuestionType === 'multiple_choice' ? newWrongAnswers : [],
        explanation: newExplanation || undefined,
      };

      // Add type-specific fields
      if (newQuestionType === 'fill_blank') {
        questionData.blankText = newBlankText;
        questionData.acceptableAnswers = newAcceptableAnswers.filter(a => a.trim());
      } else if (newQuestionType === 'essay') {
        questionData.expectedKeyPoints = newExpectedKeyPoints.filter(p => p.trim());
        questionData.minWords = newMinWords;
        questionData.maxWords = newMaxWords;
        questionData.correctAnswer = ''; // Essays don't have a correct answer
      }

      if (editingQuestionIndex !== null) {
        // Edit existing question
        updatedQuestions[editingQuestionIndex] = {
          ...updatedQuestions[editingQuestionIndex],
          ...questionData,
        };
      } else {
        // Add new question
        updatedQuestions.push({
          id: Date.now().toString(),
          ...questionData,
        });
      }

      // Remove 'id' field from questions before sending to backend (backend generates its own IDs)
      const questionsForBackend = updatedQuestions.map(({ id, ...rest }) => rest);

      await quizService.updateQuiz(id!, {
        questions: questionsForBackend,
      });

      toast.success(editingQuestionIndex !== null ? 'Questão atualizada com sucesso!' : 'Questão criada com sucesso!');
      handleCloseQuestionModal();
      loadQuiz();
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error('Erro ao salvar questão');
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleDeleteQuestion = async (index: number) => {
    if (!window.confirm('Tem certeza que deseja deletar esta questão?')) {
      return;
    }

    try {
      const updatedQuestions = quiz!.questions.filter((_, i) => i !== index);
      
      // Remove 'id' field from questions before sending to backend
      const questionsForBackend = updatedQuestions.map(({ id, ...rest }) => rest);
      
      await quizService.updateQuiz(id!, {
        questions: questionsForBackend,
      });
      toast.success('Questão deletada com sucesso!');
      loadQuiz();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Erro ao deletar questão');
    }
  };

  // Apply client-side filtering and sorting for questions
  const getFilteredAndSortedQuestions = () => {
    if (!quiz || !quiz.questions) return [];
    
    let filtered = [...quiz.questions];

    // Apply search filter (strip HTML so bold/italic formatting doesn't break matching)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (text?: string) => !!text && stripHtmlTags(text).toLowerCase().includes(searchLower);
      filtered = filtered.filter(question =>
        matchesSearch(question.question) ||
        matchesSearch(question.correctAnswer) ||
        question.wrongAnswers?.some((a: string) => matchesSearch(a)) ||
        matchesSearch(question.explanation)
      );
    }

    // Apply question type filter
    if (questionTypeFilter !== 'all') {
      filtered = filtered.filter(question => question.questionType === questionTypeFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let compareValue = 0;
      
      switch (sortBy) {
        case 'order':
          // Keep original order using index
          const indexA = quiz.questions.findIndex(q => q.id === a.id);
          const indexB = quiz.questions.findIndex(q => q.id === b.id);
          compareValue = indexA - indexB;
          break;
        case 'question':
          compareValue = a.question.localeCompare(b.question);
          break;
        case 'type':
          compareValue = (a.questionType || 'multiple_choice').localeCompare(b.questionType || 'multiple_choice');
          break;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });

    return filtered;
  };

  const displayQuestions = quiz ? getFilteredAndSortedQuestions() : [];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-gray-600 dark:text-gray-400">Quiz não encontrado</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-all duration-500 px-px md:p-6">
      {/* Header */}
      <div className="mb-6">
        {/* Mobile-style back button and title */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/quizzes')}
                className="inline-flex items-center p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 mr-3"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-tight">
                {quiz.title}
              </h1>
            </div>
            
            {/* Question count and action buttons - shown on desktop */}
            <div className="hidden sm:flex items-center space-x-2">
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{quiz.questions.length} {quiz.questions.length === 1 ? 'questão' : 'questões'}</span>
              </div>
              
              {/* Edit and Delete buttons */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleTogglePublic}
                  disabled={togglingPublic}
                  className={`p-2 rounded-lg transition-all ${togglingPublic ? 'opacity-50 cursor-not-allowed' : ''} ${
                    quiz.isPublic 
                      ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                  }`}
                  title={quiz.isPublic ? 'Quiz Público - Clique para tornar privado' : 'Quiz Privado - Clique para tornar público'}
                >
                  {quiz.isPublic ? (
                    <GlobeAltIcon className="h-5 w-5" />
                  ) : (
                    <LockClosedIcon className="h-5 w-5" />
                  )}
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-all"
                  title="Editar Quiz"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                  title="Deletar Quiz"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          
          {/* Question count and action buttons - shown on mobile */}
          <div className="sm:hidden ml-14 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{quiz.questions.length} {quiz.questions.length === 1 ? 'questão' : 'questões'}</span>
              </div>
              
              {/* Edit and Delete buttons - mobile */}
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleTogglePublic}
                  disabled={togglingPublic}
                  className={`p-2 rounded-lg transition-all ${togglingPublic ? 'opacity-50 cursor-not-allowed' : ''} ${
                    quiz.isPublic 
                      ? 'text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30' 
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                  }`}
                  title={quiz.isPublic ? 'Quiz Público - Clique para tornar privado' : 'Quiz Privado - Clique para tornar público'}
                >
                  {quiz.isPublic ? (
                    <GlobeAltIcon className="h-5 w-5" />
                  ) : (
                    <LockClosedIcon className="h-5 w-5" />
                  )}
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-all"
                  title="Editar Quiz"
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                  title="Deletar Quiz"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Description */}
          {quiz.description && (
            <p className="text-gray-700 dark:text-gray-300 mt-4 text-sm sm:text-base bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-xl p-4 shadow-lg">
              {quiz.description}
            </p>
          )}

          {/* Action Buttons Row */}
          <div className="mt-4 flex flex-wrap gap-3">
            {/* Add Question Button */}
            <button
              onClick={() => setShowCreateQuestionModal(true)}
              className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-transparent text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg touch-target hover:scale-105 text-white bg-gradient-to-r from-purple-500 via-pink-500 to-purple-600 hover:from-purple-600 hover:via-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-900 shadow-purple-500/25 hover:shadow-purple-600/30 backdrop-blur-sm"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Adicionar Questão
            </button>
            
            {/* Start Quiz Button */}
            {quiz.questions.length > 0 && (
              <button
                onClick={() => navigate(`/quiz/${id}`)}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-transparent text-sm font-semibold rounded-xl transition-all duration-200 shadow-lg touch-target text-white bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-600 hover:from-emerald-600 hover:via-green-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 dark:focus:ring-offset-gray-900 hover:scale-105 shadow-emerald-500/25 hover:shadow-emerald-600/30 backdrop-blur-sm"
              >
                <PlayIcon className="w-4 h-4 mr-2" />
                Estudar Quiz
              </button>
            )}
            
            {/* Options Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowQuizOptions(!showQuizOptions)}
                className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-3 sm:py-2 border border-gray-200/50 dark:border-gray-600/50 text-sm font-semibold rounded-xl text-gray-700 dark:text-gray-200 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 transition-all duration-200 shadow-lg hover:shadow-xl touch-target backdrop-blur-sm hover:scale-105"
                title="Mostrar opções do quiz"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
                Opções
              </button>
              
              {/* Dropdown menu */}
              {showQuizOptions && (
                <>
                  {/* Backdrop to close dropdown when clicking outside */}
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowQuizOptions(false)}
                  ></div>
                  
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 z-20 overflow-hidden">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setShowStatisticsModal(true);
                          setShowQuizOptions(false);
                        }}
                        className="w-full flex items-center px-4 py-3 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Ver Estatísticas
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Mode Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-6 border border-gray-200 dark:border-gray-700 max-w-2xl shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-xl bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Editar Quiz
              </h3>
              <button
                onClick={() => setIsEditing(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Título
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all"
                  placeholder="Título do quiz"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all resize-none"
                  placeholder="Descrição do quiz (opcional)"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Tags
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {editTags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-2 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200"
                      >
                      <XMarkIcon className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all"
                    placeholder="Adicionar tag"
                  />
                  <button
                    onClick={handleAddTag}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Adicionar
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setIsEditing(false)}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium transition-all"
              >
                Salvar Alterações
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Questions List (only for custom quizzes) */}
        {!isEditing && quiz.type === 'custom' && (
          <>
            {/* Search Bar with Filter Button */}
            <div className="mb-4 space-y-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar questões..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-10 py-3 sm:py-2 border border-gray-200/50 dark:border-gray-600/50 rounded-2xl leading-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 dark:focus:ring-indigo-400/50 dark:focus:border-indigo-400/50 transition-all duration-300 shadow-lg dark:shadow-xl hover:shadow-xl dark:hover:shadow-2xl text-base sm:text-sm"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center touch-target"
                    >
                      <svg className="h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Filter Toggle Button */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-3 sm:py-2 rounded-2xl transition-all shadow-lg hover:shadow-xl flex items-center gap-2 ${
                    showFilters || questionTypeFilter !== 'all'
                      ? 'bg-purple-600 text-white'
                      : 'bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-600/50'
                  }`}
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  <span className="hidden sm:inline text-sm font-medium">Filtros</span>
                </button>
              </div>

              {/* Expandable Filters Panel */}
              {showFilters && (
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 dark:border-gray-600/50 shadow-xl p-4 space-y-4">
                  {/* Question Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tipo de Questão
                    </label>
                    <select
                      value={questionTypeFilter}
                      onChange={(e) => setQuestionTypeFilter(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="all">Todos os Tipos</option>
                      <option value="multiple_choice">Múltipla Escolha</option>
                      <option value="true_false">Verdadeiro/Falso</option>
                      <option value="fill_blank">Preencher Lacuna</option>
                      <option value="short_answer">Resposta Curta</option>
                    </select>
                  </div>

                  {/* Sort Options */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ordenar por
                      </label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as 'order' | 'question' | 'type')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="order">Ordem Original</option>
                        <option value="question">Questão (A-Z)</option>
                        <option value="type">Tipo</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ordem
                      </label>
                      <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="asc">Crescente</option>
                        <option value="desc">Decrescente</option>
                      </select>
                    </div>
                  </div>

                  {/* Clear All Filters */}
                  {(questionTypeFilter !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setQuestionTypeFilter('all');
                        setSortBy('order');
                        setSortOrder('asc');
                      }}
                      className="w-full px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      Limpar Todos os Filtros
                    </button>
                  )}
                </div>
              )}

              {/* Active Filters Display */}
              {questionTypeFilter !== 'all' && (
                <div className="flex flex-wrap items-center gap-2 px-2">
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Filtros ativos:</span>
                  
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs font-medium">
                    {questionTypeFilter === 'multiple_choice' ? 'Múltipla Escolha' : 
                     questionTypeFilter === 'true_false' ? 'Verdadeiro/Falso' :
                     questionTypeFilter === 'fill_blank' ? 'Preencher Lacuna' : 'Resposta Curta'}
                    <button onClick={() => setQuestionTypeFilter('all')} className="hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5">
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                </div>
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50">
              <div className="px-6 py-4 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-gray-50/50 via-white/30 to-gray-50/50 dark:from-gray-700/30 dark:via-gray-800/30 dark:to-gray-700/30 rounded-t-2xl backdrop-blur-sm">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
                    Questões ({displayQuestions.length > 0 ? `${displayQuestions.length} exibidas` : '0'})
                  </h3>
                  {(searchTerm || questionTypeFilter !== 'all') && displayQuestions.length !== quiz.questions.length && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      de {quiz.questions.length} total
                    </span>
                  )}
                </div>
              </div>

            {displayQuestions.length === 0 && quiz.questions.length > 0 ? (
              <div className="text-center py-12 px-4">
                <div className="text-gray-500 dark:text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Nenhuma questão encontrada</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {searchTerm 
                    ? `Nenhuma questão corresponde à sua busca por "${searchTerm}".`
                    : 'Nenhuma questão corresponde aos filtros selecionados.'
                  } Tente ajustar seus filtros.
                </p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setQuestionTypeFilter('all');
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-200/50 dark:border-gray-600/50 text-sm font-semibold rounded-xl text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 shadow-lg hover:shadow-xl backdrop-blur-sm hover:scale-105 transition-all duration-200"
                >
                  Limpar Filtros
                </button>
              </div>
            ) : quiz.questions.length === 0 ? (
              <div className="text-center py-12 px-4">
                <div className="text-gray-500 dark:text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Ainda não há questões</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">Comece adicionando sua primeira questão a este quiz.</p>
                <button
                  onClick={() => handleOpenCreateQuestionModal()}
                  className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-purple-500 via-pink-500 to-purple-600 hover:from-purple-600 hover:via-pink-600 hover:to-purple-700 shadow-lg shadow-purple-500/25 transition-all duration-200 hover:scale-105"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Adicionar Primeira Questão
                </button>
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {displayQuestions.map((question, index) => {
                    const originalIndex = quiz.questions.findIndex(q => q.id === question.id);
                    return (
                  <div
                    key={question.id}
                    className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 pr-4">
                        <p className="font-medium text-gray-900 dark:text-white mb-2">
                          <span className="text-purple-600 dark:text-purple-400 mr-2">{originalIndex + 1}.</span>
                          {question.question}
                        </p>
                        <div className="mt-3 space-y-1">
                          <p className="text-sm text-green-600 dark:text-green-400">
                            ✓ {question.correctAnswer}
                          </p>
                          {question.wrongAnswers.map((answer, i) => (
                            <p key={i} className="text-sm text-gray-500 dark:text-gray-400">
                              ✗ {answer}
                            </p>
                          ))}
                        </div>
                        {question.explanation && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 italic">
                            💡 {question.explanation}
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleOpenEditQuestionModal(originalIndex)}
                          className="p-2 text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-all"
                          title="Editar questão"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteQuestion(originalIndex)}
                          className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 transition-all"
                          title="Deletar questão"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                    );
                  })}
              </div>
            )}
            </div>
          </>
        )}

        {/* Questions info for deck-based quizzes */}
        {!isEditing && quiz.type === 'deck-based' && quiz.questions.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Questões ({quiz.questions.length})
            </h2>
            <div className="space-y-4">
              {quiz.questions.map((question, index) => (
                <div
                  key={question.id}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
                >
                  <p className="font-medium text-gray-900 dark:text-white mb-2">
                    {index + 1}. {question.question}
                  </p>
                  {question.explanation && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      💡 {question.explanation}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Statistics Modal */}
      {showStatisticsModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-6 border border-gray-200 dark:border-gray-700 max-w-md shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-xl bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Estatísticas do Quiz
              </h3>
              <button
                onClick={() => setShowStatisticsModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Attempts */}
              <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200/50 dark:border-blue-700/50">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-500/20 dark:bg-blue-500/30 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Tentativas</span>
                </div>
                <span className="text-2xl font-bold text-blue-900 dark:text-blue-100">{quiz.attemptsCount}</span>
              </div>
              
              {/* Best Score */}
              <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200/50 dark:border-green-700/50">
                <div className="flex items-center">
                  <div className="p-2 bg-green-500/20 dark:bg-green-500/30 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">Melhor Nota</span>
                </div>
                <span className="text-2xl font-bold text-green-900 dark:text-green-100">{quiz.bestScore}%</span>
              </div>
              
              {/* Average Score */}
              <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200/50 dark:border-purple-700/50">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-500/20 dark:bg-purple-500/30 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Média</span>
                </div>
                <span className="text-2xl font-bold text-purple-900 dark:text-purple-100">{quiz.averageScore}%</span>
              </div>
              
              {/* Questions Count */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200/50 dark:border-gray-600/50">
                <div className="flex items-center">
                  <div className="p-2 bg-gray-500/20 dark:bg-gray-500/30 rounded-lg mr-3">
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total de Questões</span>
                </div>
                <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{quiz.questions.length}</span>
              </div>
              
              {/* Next Review */}
              <div className={`flex items-center justify-between p-4 rounded-xl border ${
                new Date(quiz.nextReview) <= new Date()
                  ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200/50 dark:border-orange-700/50'
                  : 'bg-gray-50 dark:bg-gray-700/50 border-gray-200/50 dark:border-gray-600/50'
              }`}>
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg mr-3 ${
                    new Date(quiz.nextReview) <= new Date()
                      ? 'bg-orange-500/20 dark:bg-orange-500/30'
                      : 'bg-gray-500/20 dark:bg-gray-500/30'
                  }`}>
                    <svg className={`w-5 h-5 ${
                      new Date(quiz.nextReview) <= new Date()
                        ? 'text-orange-600 dark:text-orange-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span className={`text-sm font-medium ${
                    new Date(quiz.nextReview) <= new Date()
                      ? 'text-orange-700 dark:text-orange-300'
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {new Date(quiz.nextReview) <= new Date() ? 'Pronto para Revisar!' : 'Próxima Revisão'}
                  </span>
                </div>
                <span className={`text-lg font-bold ${
                  new Date(quiz.nextReview) <= new Date()
                    ? 'text-orange-900 dark:text-orange-100'
                    : 'text-gray-900 dark:text-gray-100'
                }`}>
                  {new Date(quiz.nextReview) <= new Date() 
                    ? 'Agora' 
                    : new Date(quiz.nextReview).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                  }
                </span>
              </div>
            </div>
            
            <div className="mt-6">
              <button
                onClick={() => setShowStatisticsModal(false)}
                className="w-full px-4 py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-600 hover:from-purple-600 hover:via-pink-600 hover:to-purple-700 text-white font-medium rounded-xl shadow-lg transition-all duration-200 hover:scale-105"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-96 shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4">
                Deletar Quiz?
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tem certeza que deseja deletar este quiz? Esta ação não pode ser desfeita.
                </p>
              </div>
              <div className="flex justify-center space-x-3 mt-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteQuiz}
                  className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                >
                  Deletar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Question Modal */}
      {showCreateQuestionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
          <div className="relative top-10 mx-auto p-6 border border-gray-200 dark:border-gray-700 max-w-2xl shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-xl bg-white dark:bg-gray-800 my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {editingQuestionIndex !== null ? 'Editar Questão' : 'Nova Questão'}
              </h3>
              <button
                onClick={handleCloseQuestionModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Question Type Selector - only show when creating new question */}
              {editingQuestionIndex === null && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Tipo de Questão
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setNewQuestionType('multiple_choice')}
                      className={`px-4 py-3 text-sm font-medium rounded-xl border-2 transition-all duration-200 flex items-center justify-center gap-2 ${
                        newQuestionType === 'multiple_choice'
                          ? 'border-purple-500 bg-purple-100 text-purple-800 dark:bg-purple-500/20 dark:text-purple-200 dark:border-purple-400 shadow-md shadow-purple-500/20'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-purple-300 hover:bg-purple-50 dark:hover:border-purple-500 dark:hover:bg-purple-500/10 bg-white dark:bg-gray-700/50'
                      }`}
                    >
                      📝 Múltipla Escolha
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewQuestionType('true_false')}
                      className={`px-4 py-3 text-sm font-medium rounded-xl border-2 transition-all duration-200 flex items-center justify-center gap-2 ${
                        newQuestionType === 'true_false'
                          ? 'border-green-500 bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-200 dark:border-green-400 shadow-md shadow-green-500/20'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-green-300 hover:bg-green-50 dark:hover:border-green-500 dark:hover:bg-green-500/10 bg-white dark:bg-gray-700/50'
                      }`}
                    >
                      ✓✗ Verdadeiro ou Falso
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewQuestionType('fill_blank')}
                      className={`px-4 py-3 text-sm font-medium rounded-xl border-2 transition-all duration-200 flex items-center justify-center gap-2 ${
                        newQuestionType === 'fill_blank'
                          ? 'border-amber-500 bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 dark:border-amber-400 shadow-md shadow-amber-500/20'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-amber-300 hover:bg-amber-50 dark:hover:border-amber-500 dark:hover:bg-amber-500/10 bg-white dark:bg-gray-700/50'
                      }`}
                    >
                      📄 Preencher Lacuna
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewQuestionType('essay')}
                      className={`px-4 py-3 text-sm font-medium rounded-xl border-2 transition-all duration-200 flex items-center justify-center gap-2 ${
                        newQuestionType === 'essay'
                          ? 'border-blue-500 bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200 dark:border-blue-400 shadow-md shadow-blue-500/20'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-blue-300 hover:bg-blue-50 dark:hover:border-blue-500 dark:hover:bg-blue-500/10 bg-white dark:bg-gray-700/50'
                      }`}
                    >
                      ✍️ Dissertativa
                    </button>
                  </div>
                </div>
              )}

              {/* Question Type Badge when editing */}
              {editingQuestionIndex !== null && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Tipo:</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    newQuestionType === 'multiple_choice' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' :
                    newQuestionType === 'true_false' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                    newQuestionType === 'fill_blank' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  }`}>
                    {newQuestionType === 'multiple_choice' ? 'Múltipla Escolha' :
                     newQuestionType === 'true_false' ? 'V ou F' :
                     newQuestionType === 'fill_blank' ? 'Preencher Lacuna' : 'Dissertativa'}
                  </span>
                </div>
              )}

              {/* Question */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Pergunta *
                </label>
                <textarea
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all resize-none"
                  placeholder="Digite a pergunta..."
                />
              </div>

              {/* MULTIPLE CHOICE fields */}
              {newQuestionType === 'multiple_choice' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Resposta Correta *
                    </label>
                    <input
                      type="text"
                      value={newCorrectAnswer}
                      onChange={(e) => setNewCorrectAnswer(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all"
                      placeholder="Resposta correta"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Respostas Incorretas * (3 opções)
                    </label>
                    <div className="space-y-2">
                      {[0, 1, 2].map((index) => (
                        <input
                          key={index}
                          type="text"
                          value={newWrongAnswers[index]}
                          onChange={(e) => {
                            const updated = [...newWrongAnswers];
                            updated[index] = e.target.value;
                            setNewWrongAnswers(updated);
                          }}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all"
                          placeholder={`Resposta incorreta ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* TRUE/FALSE fields */}
              {newQuestionType === 'true_false' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Resposta Correta *
                  </label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setNewCorrectAnswer('Verdadeiro')}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                        newCorrectAnswer === 'Verdadeiro'
                          ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          : 'border-gray-300 dark:border-gray-600 hover:border-green-300 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      ✓ Verdadeiro
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewCorrectAnswer('Falso')}
                      className={`flex-1 px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                        newCorrectAnswer === 'Falso'
                          ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : 'border-gray-300 dark:border-gray-600 hover:border-red-300 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      ✗ Falso
                    </button>
                  </div>
                </div>
              )}

              {/* FILL IN THE BLANK fields */}
              {newQuestionType === 'fill_blank' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Texto com Lacuna * (use ___ para marcar)
                    </label>
                    <input
                      type="text"
                      value={newBlankText}
                      onChange={(e) => setNewBlankText(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      placeholder="Ex: O ___ é a capital do Brasil."
                    />
                    <p className="text-xs text-gray-500 mt-1">Use três underscores (___) para indicar onde a resposta deve ser inserida.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Resposta Correta *
                    </label>
                    <input
                      type="text"
                      value={newCorrectAnswer}
                      onChange={(e) => setNewCorrectAnswer(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                      placeholder="Ex: Brasília"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Respostas Alternativas Aceitas (opcional)
                    </label>
                    <div className="space-y-2">
                      {newAcceptableAnswers.map((answer, idx) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="text"
                            value={answer}
                            onChange={(e) => {
                              const updated = [...newAcceptableAnswers];
                              updated[idx] = e.target.value;
                              setNewAcceptableAnswers(updated);
                            }}
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="Resposta alternativa..."
                          />
                          <button
                            type="button"
                            onClick={() => setNewAcceptableAnswers(newAcceptableAnswers.filter((_, i) => i !== idx))}
                            className="px-2 text-red-600 hover:text-red-700"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setNewAcceptableAnswers([...newAcceptableAnswers, ''])}
                        className="text-sm text-yellow-600 hover:text-yellow-700 dark:text-yellow-400"
                      >
                        + Adicionar resposta alternativa
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* ESSAY fields */}
              {newQuestionType === 'essay' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Pontos-chave Esperados * (a IA verificará se foram abordados)
                    </label>
                    <div className="space-y-2">
                      {newExpectedKeyPoints.map((point, idx) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="text"
                            value={point}
                            onChange={(e) => {
                              const updated = [...newExpectedKeyPoints];
                              updated[idx] = e.target.value;
                              setNewExpectedKeyPoints(updated);
                            }}
                            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder={`Ponto-chave ${idx + 1}...`}
                          />
                          {newExpectedKeyPoints.length > 1 && (
                            <button
                              type="button"
                              onClick={() => setNewExpectedKeyPoints(newExpectedKeyPoints.filter((_, i) => i !== idx))}
                              className="px-2 text-red-600 hover:text-red-700"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setNewExpectedKeyPoints([...newExpectedKeyPoints, ''])}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        + Adicionar ponto-chave
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Mín. Palavras
                      </label>
                      <input
                        type="number"
                        value={newMinWords}
                        onChange={(e) => setNewMinWords(parseInt(e.target.value) || 10)}
                        min={10}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Máx. Palavras
                      </label>
                      <input
                        type="number"
                        value={newMaxWords}
                        onChange={(e) => setNewMaxWords(parseInt(e.target.value) || 500)}
                        min={50}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Explanation - common to all types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Explicação (opcional)
                </label>
                <textarea
                  value={newExplanation}
                  onChange={(e) => setNewExplanation(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all resize-none"
                  placeholder="Explicação da resposta..."
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleCloseQuestionModal}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={savingQuestion}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveQuestion}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={savingQuestion || !newQuestion.trim() || (
                  newQuestionType === 'multiple_choice' ? (!newCorrectAnswer.trim() || newWrongAnswers.filter(a => a.trim()).length < 3) :
                  newQuestionType === 'true_false' ? (newCorrectAnswer !== 'Verdadeiro' && newCorrectAnswer !== 'Falso') :
                  newQuestionType === 'fill_blank' ? (!newBlankText.includes('___') || !newCorrectAnswer.trim()) :
                  newQuestionType === 'essay' ? (newExpectedKeyPoints.filter(p => p.trim()).length === 0) : true
                )}
              >
                {savingQuestion ? 'Salvando...' : (editingQuestionIndex !== null ? 'Salvar Alterações' : 'Criar Questão')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizDetail;
